/**
 * Advisor Conversations Hook
 *
 * Manages AI investment advisor conversations in Supabase.
 * Provides:
 * - Conversation CRUD operations
 * - Message management
 * - History retrieval for AI context
 * - Recommendation outcome tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';

// Types
export interface AdvisorConversation {
  id: string;
  user_id: string;
  title: string;
  portfolio_snapshot: PortfolioSnapshot;
  status: 'active' | 'archived';
  source: 'widget' | 'page';
  language: 'en' | 'fr';
  message_count: number;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

export interface AdvisorMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata: MessageMetadata;
  contains_recommendation: boolean;
  recommendation_type?: string;
  tokens_mentioned?: string[];
  outcome_rating?: number;
  outcome_notes?: string;
  outcome_updated_at?: string;
  created_at: string;
}

export interface PortfolioSnapshot {
  total_value?: number;
  token_count?: number;
  top_tokens?: Array<{ symbol: string; value: number; pl_percent: number }>;
  pl_percent?: number;
  risk_score?: number;
}

export interface MessageMetadata {
  model_used?: string;
  tokens_used?: number;
  response_time_ms?: number;
  fallback_used?: boolean;
}

export interface HistorySummary {
  conversation_count: number;
  recent_topics: string[];
  recent_recommendations: Array<{
    type: string;
    tokens: string[];
    date: string;
    outcome: number | null;
  }>;
  successful_recommendations: number;
  poor_recommendations: number;
}

export interface UseAdvisorConversationsReturn {
  // State
  conversations: AdvisorConversation[];
  loading: boolean;
  error: Error | null;

  // Conversation actions
  fetchConversations: () => Promise<void>;
  createConversation: (data: {
    title: string;
    portfolioSnapshot?: PortfolioSnapshot;
    source?: 'widget' | 'page';
    language?: 'en' | 'fr';
  }) => Promise<AdvisorConversation | null>;
  updateConversation: (id: string, updates: Partial<AdvisorConversation>) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  archiveConversation: (id: string) => Promise<void>;

  // Message actions
  fetchMessages: (conversationId: string) => Promise<AdvisorMessage[]>;
  addMessage: (data: {
    conversationId: string;
    role: 'user' | 'assistant';
    content: string;
    metadata?: MessageMetadata;
    containsRecommendation?: boolean;
    recommendationType?: string;
    tokensMentioned?: string[];
  }) => Promise<AdvisorMessage | null>;
  updateMessageOutcome: (messageId: string, rating: number, notes?: string) => Promise<void>;

  // History for AI context
  getHistorySummary: () => Promise<HistorySummary | null>;
  getRecentRecommendations: (limit?: number) => Promise<AdvisorMessage[]>;

  // Utility
  isAuthenticated: boolean;
}

// Local storage key for fallback
const LOCAL_STORAGE_KEY = 'advisor_conversations_local';

export function useAdvisorConversations(): UseAdvisorConversationsReturn {
  const [conversations, setConversations] = useState<AdvisorConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { user, isAuthenticated } = useAuthStore();

  // Fetch all conversations for the user
  const fetchConversations = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage for non-authenticated users
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          setConversations(JSON.parse(stored));
        }
      } catch (e) {
        console.error('Error loading local conversations:', e);
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('advisor_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;

      setConversations(data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch conversations'));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Load conversations on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Create a new conversation
  const createConversation = useCallback(async (data: {
    title: string;
    portfolioSnapshot?: PortfolioSnapshot;
    source?: 'widget' | 'page';
    language?: 'en' | 'fr';
  }): Promise<AdvisorConversation | null> => {
    if (!isAuthenticated || !user?.id) {
      // Fallback: create locally
      const localConversation: AdvisorConversation = {
        id: `local_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        user_id: 'local',
        title: data.title,
        portfolio_snapshot: data.portfolioSnapshot || {},
        status: 'active',
        source: data.source || 'widget',
        language: data.language || 'en',
        message_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_message_at: new Date().toISOString(),
      };

      const updatedConversations = [localConversation, ...conversations];
      setConversations(updatedConversations);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedConversations.slice(0, 50)));

      return localConversation;
    }

    try {
      const { data: newConversation, error: insertError } = await supabase
        .from('advisor_conversations')
        .insert({
          user_id: user.id,
          title: data.title,
          portfolio_snapshot: data.portfolioSnapshot || {},
          source: data.source || 'widget',
          language: data.language || 'en',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setConversations(prev => [newConversation, ...prev]);
      return newConversation;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(err instanceof Error ? err : new Error('Failed to create conversation'));
      return null;
    }
  }, [isAuthenticated, user?.id, conversations]);

  // Update a conversation
  const updateConversation = useCallback(async (id: string, updates: Partial<AdvisorConversation>) => {
    if (!isAuthenticated || !user?.id) {
      // Update locally
      setConversations(prev => {
        const updated = prev.map(c =>
          c.id === id ? { ...c, ...updates, updated_at: new Date().toISOString() } : c
        );
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated.slice(0, 50)));
        return updated;
      });
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('advisor_conversations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setConversations(prev =>
        prev.map(c => (c.id === id ? { ...c, ...updates, updated_at: new Date().toISOString() } : c))
      );
    } catch (err) {
      console.error('Error updating conversation:', err);
      setError(err instanceof Error ? err : new Error('Failed to update conversation'));
    }
  }, [isAuthenticated, user?.id]);

  // Delete a conversation
  const deleteConversation = useCallback(async (id: string) => {
    if (!isAuthenticated || !user?.id) {
      // Delete locally
      setConversations(prev => {
        const updated = prev.filter(c => c.id !== id);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('advisor_conversations')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      setConversations(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete conversation'));
    }
  }, [isAuthenticated, user?.id]);

  // Archive a conversation
  const archiveConversation = useCallback(async (id: string) => {
    await updateConversation(id, { status: 'archived' });
  }, [updateConversation]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId: string): Promise<AdvisorMessage[]> => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage
      try {
        const stored = localStorage.getItem(`advisor_messages_${conversationId}`);
        return stored ? JSON.parse(stored) : [];
      } catch {
        return [];
      }
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('advisor_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Error fetching messages:', err);
      return [];
    }
  }, [isAuthenticated, user?.id]);

  // Add a message to a conversation
  const addMessage = useCallback(async (data: {
    conversationId: string;
    role: 'user' | 'assistant';
    content: string;
    metadata?: MessageMetadata;
    containsRecommendation?: boolean;
    recommendationType?: string;
    tokensMentioned?: string[];
  }): Promise<AdvisorMessage | null> => {
    if (!isAuthenticated || !user?.id) {
      // Save locally
      const localMessage: AdvisorMessage = {
        id: `local_msg_${Date.now()}`,
        conversation_id: data.conversationId,
        role: data.role,
        content: data.content,
        metadata: data.metadata || {},
        contains_recommendation: data.containsRecommendation || false,
        recommendation_type: data.recommendationType,
        tokens_mentioned: data.tokensMentioned,
        created_at: new Date().toISOString(),
      };

      try {
        const stored = localStorage.getItem(`advisor_messages_${data.conversationId}`);
        const messages: AdvisorMessage[] = stored ? JSON.parse(stored) : [];
        messages.push(localMessage);
        localStorage.setItem(`advisor_messages_${data.conversationId}`, JSON.stringify(messages));
      } catch (e) {
        console.error('Error saving local message:', e);
      }

      return localMessage;
    }

    try {
      const { data: newMessage, error: insertError } = await supabase
        .from('advisor_messages')
        .insert({
          conversation_id: data.conversationId,
          role: data.role,
          content: data.content,
          metadata: data.metadata || {},
          contains_recommendation: data.containsRecommendation || false,
          recommendation_type: data.recommendationType,
          tokens_mentioned: data.tokensMentioned,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      return newMessage;
    } catch (err) {
      console.error('Error adding message:', err);
      return null;
    }
  }, [isAuthenticated, user?.id]);

  // Update message outcome rating
  const updateMessageOutcome = useCallback(async (messageId: string, rating: number, notes?: string) => {
    if (!isAuthenticated || !user?.id) return;

    try {
      const { error: updateError } = await supabase
        .from('advisor_messages')
        .update({
          outcome_rating: rating,
          outcome_notes: notes,
          outcome_updated_at: new Date().toISOString(),
        })
        .eq('id', messageId);

      if (updateError) throw updateError;
    } catch (err) {
      console.error('Error updating message outcome:', err);
    }
  }, [isAuthenticated, user?.id]);

  // Get history summary for AI context
  const getHistorySummary = useCallback(async (): Promise<HistorySummary | null> => {
    if (!isAuthenticated || !user?.id) return null;

    try {
      const { data, error: rpcError } = await supabase
        .rpc('get_advisor_history_summary', { p_user_id: user.id });

      if (rpcError) throw rpcError;
      return data as HistorySummary;
    } catch (err) {
      console.error('Error getting history summary:', err);
      return null;
    }
  }, [isAuthenticated, user?.id]);

  // Get recent recommendations
  const getRecentRecommendations = useCallback(async (limit = 10): Promise<AdvisorMessage[]> => {
    if (!isAuthenticated || !user?.id) return [];

    try {
      const { data, error: rpcError } = await supabase
        .rpc('get_user_recent_recommendations', { p_user_id: user.id, p_limit: limit });

      if (rpcError) throw rpcError;
      return (data || []) as AdvisorMessage[];
    } catch (err) {
      console.error('Error getting recent recommendations:', err);
      return [];
    }
  }, [isAuthenticated, user?.id]);

  return {
    conversations,
    loading,
    error,
    fetchConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    archiveConversation,
    fetchMessages,
    addMessage,
    updateMessageOutcome,
    getHistorySummary,
    getRecentRecommendations,
    isAuthenticated,
  };
}

// Export types for use elsewhere
export type { AdvisorConversation as Conversation, AdvisorMessage as Message };
